import {
  isMosaicTrafficLightsValue,
  isMosaicTrafficLightSwitch,
  isObjectRecord,
  MosaicTrafficLightsValue,
  MosaicTrafficLightSwitch,
} from "aic";
import {
  isTlfLinkLocation,
  TlfLinkLocation,
} from "components/tlfpage/tlfheaderinfo/TlfLinkLocation";
import { isVariantTemplate, VariantTemplate } from "functions/tlf/isVariantTemplate";
import { eHierarchy, eHierarchyLevel, eLinkType, eReleaseStatus, eTlfItemType } from "../enums";

export interface ITlfItemRelationship extends Record<string, unknown> {
  azsolId: string;
  id: number;
  linkId: string;
  linkLocation?: TlfLinkLocation;
  linkStatus?: eReleaseStatus;
  linkType: eLinkType;
  version: number;
}

/**
 * Type guard that asserts whether a value conforms to the ITlfItemRelationship shape.
 *
 * @param obj - The object to validate as an ITlfItemRelationship
 * @returns boolean
 */
export const isITlfItemRelationship = (obj: unknown): obj is ITlfItemRelationship => {
  if (!isObjectRecord(obj)) return false;

  return (
    // Check properties types
    typeof obj.linkId === "string" &&
    typeof obj.linkType === "string" &&
    typeof obj.azsolId === "string" &&
    typeof obj.version === "number" &&
    // Check enums
    obj.linkType in eLinkType &&
    (obj.linkLocation === undefined ||
      obj.linkLocation === null ||
      isTlfLinkLocation(obj.linkLocation)) &&
    (obj.linkStatus === undefined ||
      obj.linkStatus === null ||
      (typeof obj.linkStatus === "string" && obj.linkStatus in eReleaseStatus))
  );
};

export interface HierarchyLink {
  hierarchyId: string;
  hierarchyLevel: eHierarchyLevel;
  hierarchyTag: string;
}

/**
 * Type guard that asserts whether a value conforms to the HierarchyLink shape.
 *
 * @param obj - The object to validate as a HierarchyLink
 * @returns Whether the object is a valid HierarchyLink
 */
export const isHierarchyLink = (obj: unknown): obj is HierarchyLink => {
  if (!isObjectRecord(obj)) return false;

  return (
    typeof obj.hierarchyId === "string" &&
    typeof obj.hierarchyTag === "string" &&
    Object.values(eHierarchyLevel).includes(obj.hierarchyLevel as eHierarchyLevel)
  );
};

export interface TlfTag {
  /** Unique identifier (GUID) of the tag. */
  tagId: string;
  /** Display value of the tag. */
  tagValue: string;
}

/**
 * Type guard that asserts whether a value conforms to the TlfTag shape.
 *
 * @param obj - The object to validate as a TlfTag
 * @returns Whether the object is a valid TlfTag
 */
export const isTlfTag = (obj: unknown): obj is TlfTag => {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "tagId" in obj &&
    "tagValue" in obj &&
    typeof (obj as TlfTag).tagId === "string" &&
    typeof (obj as TlfTag).tagValue === "string"
  );
};

export interface TrafficLightOverrideReason {
  /** Optional crystal report reference associated with the override. */
  crystalReference?: string;
  /** Free-text description when "Other" reason is chosen. */
  otherText: string;
  /** Predefined reason code selected by the user. */
  reasonCode: string;
}

/**
 * Type guard that asserts whether a value conforms to the TrafficLightOverrideReason shape.
 *
 * @param obj - The object to validate as a TrafficLightOverrideReason
 * @returns Whether the object is a valid TrafficLightOverrideReason
 */
export const isTrafficLightOverrideReason = (obj: unknown): obj is TrafficLightOverrideReason => {
  if (!isObjectRecord(obj)) return false;

  return (
    typeof obj.reasonCode === "string" &&
    typeof obj.otherText === "string" &&
    (obj.crystalReference === undefined || typeof obj.crystalReference === "string")
  );
};

/**
 * Main TLF item interface
 * Represents the main structure of a TLF item, including hierarchy, traffic light status, and related metadata.
 */
// does not need to be sorted alphabetically
export interface TlfItemIndexReadDto extends Record<string, unknown> {
  id: string;
  azsolId: string;
  version: number;

  primaryHierarchyId: string;
  primaryHierarchyName: string;
  primaryHierarchyLevel: eHierarchyLevel;
  primaryHierarchyType: eHierarchy;
  taPrefix: string;
  projectCode: string;
  dNumber: string;
  hierarchyLinks: HierarchyLink[];

  tocOrder?: number;
  tocNumber?: string;
  tocLabel?: string;
  analysisSetId?: string;
  /** Release status of the related Template */
  tocReleaseStatus?: eReleaseStatus;
  tocOriginTrafficLight?: MosaicTrafficLightsValue;
  coreTrafficLight: MosaicTrafficLightsValue;
  coreTrafficLightSwitch: MosaicTrafficLightSwitch;
  trafficLightOverrideReason?: TrafficLightOverrideReason;

  additionalInformation: string;
  type: eTlfItemType;
  domain: string;
  tlfTags?: TlfTag[];
  /** Persisted named variants and their patch-name references; absent on legacy items. */
  variantTemplates?: VariantTemplate[];
  /** Named variant flattened into this TLF; absent for Master and legacy items. */
  variantUsed?: string;

  releaseDate?: string;
  decommissionDate?: string;
  releaseStatus: eReleaseStatus;

  relationships: ITlfItemRelationship[];
  isBackupVersion: boolean;

  lastUpdatedFromHistory: string;
}

/**
 * Type guard that asserts whether a value conforms to the TlfItemIndexReadDto shape.
 *
 * @param obj - The object to validate as a TlfItemIndexReadDto
 * @returns Whether the object is a valid TlfItemIndexReadDto
 */
export const isTlfItemIndexReadDto = (obj: unknown): obj is TlfItemIndexReadDto => {
  // Check object
  if (typeof obj !== "object" || obj === null) return false;
  // Check props
  const chkObj = obj as TlfItemIndexReadDto;
  if (typeof chkObj.id !== "string") return false;
  if (typeof chkObj.azsolId !== "string") return false;
  if (typeof chkObj.version !== "number") return false;
  if (typeof chkObj.primaryHierarchyId !== "string") return false;
  if (typeof chkObj.primaryHierarchyName !== "string") return false;
  if (typeof chkObj.primaryHierarchyLevel !== "string") return false;
  if (typeof chkObj.primaryHierarchyType !== "string") return false;
  if (typeof chkObj.dNumber !== "string") return false;
  if (typeof chkObj.taPrefix !== "string") return false;
  if (typeof chkObj.projectCode !== "string") return false;
  if (chkObj.tocOrder !== undefined && typeof chkObj.tocOrder !== "number") return false;
  if (chkObj.tocNumber !== undefined && typeof chkObj.tocNumber !== "string") return false;
  if (chkObj.tocLabel !== undefined && typeof chkObj.tocLabel !== "string") return false;
  if (chkObj.analysisSetId !== undefined && typeof chkObj.analysisSetId !== "string") return false;
  if (chkObj.tocReleaseStatus !== undefined && typeof chkObj.tocReleaseStatus !== "string")
    return false;
  if (
    chkObj.tocOriginTrafficLight !== undefined &&
    typeof chkObj.tocOriginTrafficLight !== "string"
  )
    return false;
  if (!isMosaicTrafficLightSwitch(chkObj.coreTrafficLightSwitch)) return false;
  if (!isMosaicTrafficLightsValue(chkObj.coreTrafficLight)) return false;
  if (
    chkObj.trafficLightOverrideReason !== undefined &&
    !isTrafficLightOverrideReason(chkObj.trafficLightOverrideReason)
  )
    return false;
  if (typeof chkObj.additionalInformation !== "string") return false;
  if (typeof chkObj.type !== "string") return false;
  if (typeof chkObj.domain !== "string") return false;
  if (chkObj.releaseDate !== undefined && typeof chkObj.releaseDate !== "string") return false;
  if (chkObj.decommissionDate !== undefined && typeof chkObj.decommissionDate !== "string")
    return false;
  if (typeof chkObj.releaseStatus !== "string") return false;
  if (!Array.isArray(chkObj.relationships) || !chkObj.relationships.every(isITlfItemRelationship))
    return false;
  if (typeof chkObj.isBackupVersion !== "boolean") return false;
  if (!Array.isArray(chkObj.hierarchyLinks) || !chkObj.hierarchyLinks.every(isHierarchyLink))
    return false;
  if (typeof chkObj.lastUpdatedFromHistory !== "string") return false;
  if (
    chkObj.tlfTags !== undefined &&
    (!Array.isArray(chkObj.tlfTags) || !chkObj.tlfTags.every(isTlfTag))
  )
    return false;
  if (chkObj.variantUsed !== undefined && typeof chkObj.variantUsed !== "string") return false;
  if (
    chkObj.variantTemplates !== undefined &&
    (!Array.isArray(chkObj.variantTemplates) ||
      chkObj.variantTemplates.some((variant) => !isVariantTemplate(variant)))
  )
    return false;

  // Everything is good
  return true;
};
